import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface RetreatCarouselProps {
  title: string;
  images: string[];
  autoPlayInterval?: number;
}
export function RetreatCarousel({
  title,
  images,
  autoPlayInterval = 5000
}: RetreatCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const hasMultiple = images.length > 1;
  const goTo = useCallback((idx: number) => {
    setCurrent((idx % images.length + images.length) % images.length);
    setResetKey(k => k + 1);
  }, [images.length]);
  useEffect(() => {
    if (!hasMultiple) return;
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % images.length);
    }, autoPlayInterval);
    return () => clearInterval(timer);
  }, [resetKey, hasMultiple, images.length, autoPlayInterval]);
  return <div className="flex flex-col" data-id="element-45">
      <div className="relative w-full aspect-[3/4] overflow-hidden bg-black border border-white/10" data-id="element-46">
        <AnimatePresence mode="wait" initial={false} data-id="element-47">
          <motion.div key={current} initial={{
          opacity: 0,
          scale: 1.03
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeInOut'
        }} className="absolute inset-0" data-id="element-48">
            <img src={images[current]} alt={`${title} retreat`} className="w-full h-full object-cover" data-id="element-49" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-50" />
          </motion.div>
        </AnimatePresence>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 w-full p-4 md:p-6 text-center z-10" data-id="element-51">
          <span className="font-cinzel text-sm md:text-base lg:text-lg text-gold tracking-[0.2em] uppercase" data-id="element-52">
            {title}
          </span>
        </div>

        {/* Arrows — only when multiple images */}
        {hasMultiple && <>
            <button onClick={() => goTo(current - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full border border-white/20 bg-black/50 backdrop-blur-sm text-cream/60 hover:text-gold hover:border-gold/50 transition-all duration-300" aria-label={`Previous ${title} image`} data-id="element-53">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-54" />
            </button>
            <button onClick={() => goTo(current + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full border border-white/20 bg-black/50 backdrop-blur-sm text-cream/60 hover:text-gold hover:border-gold/50 transition-all duration-300" aria-label={`Next ${title} image`} data-id="element-55">
              <ChevronRightIcon className="w-4 h-4" data-id="element-56" />
            </button>
          </>}
      </div>

      {/* Dots — only when multiple images */}
      {hasMultiple && <div className="flex justify-center gap-2 mt-3" data-id="element-57">
          {images.map((_, idx) => <button key={idx} onClick={() => goTo(idx)} className={`w-2 h-2 rounded-full transition-all duration-400 ${idx === current ? 'bg-gold scale-125' : 'bg-white/20 hover:bg-white/40'}`} aria-label={`Go to ${title} image ${idx + 1}`} data-id="element-58" />)}
        </div>}
    </div>;
}